// Listing modes for Aircraft Roster — Month / Today / List.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/ads-and-sbs/modes.ts, itself mirrored from src/blank/BlankLayout.tsx
// (Rule 0 — the canonical reference).
//
//   <base>/month   → AircraftRosterMonth (month x aircraft grid, the legacy
//                                          scheduler's default view)
//   <base>/today   → AircraftRosterToday (ONE row per aircraft for one day,
//                                          every column: status, booking,
//                                          crew, the counts as dropdowns, the
//                                          fleet status; rows expand to crew,
//                                          bookings and flights — AR Patch 1.1
//                                          D12, which merged List into it)
//   <base>/codes   → AircraftRosterCodes (the code list: legacy's "Code
//                                          Editor" mode; M13-L3)
//
//   <base>/board   → redirects to today. Aaron 2026-10-06, of the old day
//                    board: "Replace it with Today". Old links keep working.
//   <base>/list    → redirects to today. Aaron 2026-10-11: "merge today and
//                    list view - its should be all just 1 list with all
//                    columns". Old links and a remembered "list" keep working.

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  month: "Month",
  today: "Today",
  codes: "Codes",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  month: "Month grid by aircraft",
  today: "Every aircraft, one day",
  codes: "Status and crew codes",
};

/**
 * Month, not Board or List. The legacy dhtmlxScheduler opens on its MONTH /
 * timeline view — aircraft down the side, the whole month across the top — and
 * that is the surface an operator opens this module for. Board is the
 * single-day lens over the same rows; List is the flat-table alternative.
 *
 * This CHANGED from "board" when the month view landed. A stored "board" is
 * still a valid segment, so anyone who had already picked a mode keeps it
 * (readRememberedModeSegment below); only a user with nothing stored is moved
 * to the legacy-faithful default.
 */
export const DEFAULT_MODE_SEGMENT = "month";

/**
 * `<module>.viewMode` — the exact key the retired chip+menu already used
 * (AircraftRosterList.tsx), so a stored mode from before this migration is
 * still honoured; see readRememberedModeSegment below.
 */
export const MODE_STORAGE_KEY = "aircraft_roster.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION: the retired switcher's two mode keys ("board",
 * "list") are IDENTICAL to the route segments below, so anything already
 * stored under MODE_STORAGE_KEY is already in the right shape — and both
 * remain real modes after "month" was added, so no stored value is orphaned.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  // "board" (M13-L3) and "list" (AR Patch 1.1) were folded into "today";
  // someone who had picked either keeps that view rather than the default.
  if (stored === RETIRED_BOARD_SEGMENT || stored === RETIRED_LIST_SEGMENT) return "today";
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/** The retired day-board segment — index.tsx redirects it to `today`. */
export const RETIRED_BOARD_SEGMENT = "board";

/** The retired List segment (merged into Today) — index.tsx redirects it too. */
export const RETIRED_LIST_SEGMENT = "list";

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
